import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./Home.css";
import downloadQr from "./assets/download-qr.svg";
// Updated to the compressed WebP versions at src/screenshots/.
// If Home.js doesn't live directly under src/, adjust this relative path
// to match — e.g. "../screenshots/1.webp".
import screenshot1 from "./assets/screenshots/1.webp";
import screenshot2 from "./assets/screenshots/2.webp";
import screenshot3 from "./assets/screenshots/3.webp";
import screenshot4 from "./assets/screenshots/4.webp";
import screenshot5 from "./assets/screenshots/5.webp";
import screenshot6 from "./assets/screenshots/6.webp";
import screenshot7 from "./assets/screenshots/7.webp";
import screenshot8 from "./assets/screenshots/8.webp";
import logoText from "./assets/text.webp";
import PhoneSlideshow from "./PhoneSlideshow";

/* ==========================================================================
   SEO constants — single source of truth for title/description/schema.
   Update SITE_URL once you know the canonical production domain.
   ========================================================================== */

const SITE_URL = "https://www.buzzscores.com";
const CANONICAL_URL = `${SITE_URL}/`;
const APP_NAME = "BuzzScores";

const STORE_LINKS = {
	google: "market://details?id=com.buzzscores.app",
	apple: "itms-apps://apps.apple.com/app/id6749062854",
};

const META_TITLE =
	"BuzzScores – Live Football Scores, Goal Replays & AI Predictions";
const META_DESCRIPTION =
	"Live football scores, instant goal replays and AI-powered predictions across 2,000+ leagues. In-depth stats, player ratings and win probabilities for the Premier League, Champions League, La Liga, Serie A, Bundesliga, MLS and more.";
const OG_IMAGE = `${SITE_URL}/assets/og-image.png`;

const EYEBROW = "Live Scores, Goal Replays & AI Predictions";

// The actual intrinsic pixel size of the screenshot files (all 8 share the
// same device frame, so one size covers all of them). Passed to
// PhoneSlideshow so it can set the frame's aspect-ratio synchronously on
// first render instead of detecting it after the fact — VERIFY these
// against your real files (e.g. open one in Preview/Finder "Get Info", or
// `file src/assets/screenshots/1.webp`) and correct if they're off.
const SCREENSHOT_WIDTH = 393;
const SCREENSHOT_HEIGHT = 852;

const SCREENSHOTS = [
	{
		src: screenshot1,
		alt: "BuzzScores live match screen with real-time football scores",
	},
	{
		src: screenshot2,
		alt: "BuzzScores in-depth match statistics, xG and shot analytics",
	},
	{
		src: screenshot3,
		alt: "BuzzScores AI predictions and win probability screen",
	},
	{
		src: screenshot4,
		alt: "BuzzScores personalized match feed for favorite teams and leagues",
	},
	{
		src: screenshot5,
		alt: "BuzzScores live lineups, formations and player ratings",
	},
	{
		src: screenshot6,
		alt: "BuzzScores match heatmaps and passing network stats",
	},
	{
		src: screenshot7,
		alt: "BuzzScores global league coverage and live tables",
	},
	{
		src: screenshot8,
		alt: "BuzzScores match calendar and fixture schedule",
	},
];

// Short list shown inside the hero itself (kept tight for the fixed canvas).
const FEATURES = [
	{
		icon: "⚡",
		label: "Live football scores & goal replays across 2,000+ leagues",
	},
	{
		icon: "🧠",
		label: "AI predictions & value picks that outsmart the market",
	},
	{
		icon: "📊",
		label: "Live player ratings, xG, heatmaps & shot maps",
	},
	{
		icon: "🔔",
		label: "Custom notification alerts for goals, cards, xG & live stats",
	},
	{
		icon: "🎯",
		label: "Personalized homepage filtered by form, streaks & H2H",
	},
	{
		icon: "🌍",
		label: "2,000+ leagues coverage worldwide",
	},
];

// Longer, crawlable feature copy for the below-the-fold content grid —
// this is what gives Google real text to index beyond the hero.
const FEATURE_DETAILS = [
	{
		icon: "⚡",
		title: "Live Scores & Goal Replays",
		description:
			"Real-time updates for goals, cards, substitutions and final results at stadium speed. Watch goal replays directly inside match events moments after the ball hits the net.",
	},
	{
		icon: "🔔",
		title: "Custom Live Stat Alerts",
		description:
			"Get personalized push notifications the instant a stat you're tracking updates — xG, shots on target, corners, possession swings and more — so you never have to keep the app open to stay on top of a match.",
	},
	{
		icon: "📊",
		title: "In-Depth Match Statistics",
		description:
			"Professional-level analytics for every game: possession, shots, passes, fouls and expected goals (xG), full lineups and live formations, match timelines, heatmaps and passing networks. Tap any player to instantly see their live rating and stats.",
	},
	{
		icon: "🧠",
		title: "AI Predictions & Value Picks",
		description:
			"Pre-match and live score forecasts, win/draw/loss probability based on live momentum, and value picks in the AI Insights tab where our model and the market disagree. Track scoring streaks and form trends, then sort predictions by win odds, expected goals or scoreline.",
	},
	{
		icon: "🎯",
		title: "Build Your Own Homepage",
		description:
			"Filter matches by head-to-head history, current streaks or statistical averages, so you only see the fixtures that match what you care about before kickoff.",
	},
	{
		icon: "⭐",
		title: "Personalized Match Feed",
		description:
			"Follow clubs, national teams, players or leagues and get instant access to the stats, fixtures and results that matter to you.",
	},
];

const LEAGUE_GROUPS = [
	{
		region: "Europe",
		leagues: [
			"Premier League",
			"Champions League",
			"La Liga",
			"Serie A",
			"Bundesliga",
		],
	},
	{
		region: "Americas",
		leagues: ["MLS", "Copa Libertadores", "Liga MX", "Brasileirão"],
	},
	{
		region: "Africa, Asia & Beyond",
		leagues: ["Hundreds of domestic and continental leagues worldwide"],
	},
];

const PREMIUM_FEATURES = [
	"Live and pregame AI-powered predictions",
	"Full access to the AI Insights tab, including value picks",
	"Advanced custom filtering across your homepage and match stats",
	"100% ad-free, faster load times",
	"3-day free trial",
];

// Also drives the FAQPage structured data below.
const FAQS = [
	{
		question: "Who's playing today?",
		answer:
			"Open BuzzScores for a live, personalized match feed updated in real time.",
	},
	{
		question: "What's the score right now?",
		answer:
			"Live scores update instantly with goals, cards and substitutions as they happen.",
	},
	{
		question: "Where can I watch goal replays?",
		answer:
			"Every goal is available as a replay directly from the match screen, moments after it's scored.",
	},
	{
		question: "How do I find value picks?",
		answer:
			"Open the AI Insights tab for algorithmic value picks alongside pre-match and live predictions.",
	},
	{
		question: "Can I filter matches by team form or history?",
		answer:
			"Yes — build a custom homepage filtered by head-to-head record, streaks or statistical averages.",
	},
	{
		question: "Can I see upcoming fixtures and past results?",
		answer:
			"Yes — the match calendar gives you clean daily, weekly and monthly listings, so you always know what's next.",
	},
];

// The desktop layout is laid out on this fixed canvas, then scaled to
// fit the viewport (see usePageZoomLock below). Tweak these two numbers
// if you want to change the design's base proportions.
const DESIGN_WIDTH = 1366;
const DESIGN_HEIGHT = 768;

/* ==========================================================================
Page-zoom lock. The browser shrinks window.innerWidth/innerHeight when
you zoom in, but window.outerWidth stays in screen CSS pixels — so their
ratio tracks the page-zoom factor. We expose the inverse of that factor
as the --counter-zoom CSS var and apply it as an explicit `zoom` on one
wrapper (.zoom-lock-root in Home.css) around the whole page, and make
every layout decision against the *effective* (unzoomed) viewport — so
the entire page — hero, content sections, footer — renders at exactly
the same visual size at any page-zoom level.
========================================================================== */
function getBrowserZoom() {
	const outer = window.outerWidth;
	const inner = window.innerWidth;
	if (!outer || !inner) return 1;
	const z = outer / inner;
	// Sanity clamp: anything this far off is a browser quirk (docked
	// devtools, odd popup windows), not a real zoom level.
	return z > 0.25 && z < 4 ? z : 1;
}

function usePageZoomLock() {
	React.useLayoutEffect(() => {
		const root = document.documentElement;
		let frame = 0;
		function update() {
			// One z reading, used for everything below. Previously
			// --counter-zoom and --fit-scale each called getBrowserZoom()
			// independently — if window.innerWidth/outerWidth shifted by
			// even a hair between those two reads (a layout pass, a
			// scrollbar tick), the two values no longer canceled each
			// other out *exactly*, leaving a tiny residual size error
			// that changed with the zoom level — a faint jiggle even
			// with perfect frame timing. Deriving both from a single z
			// makes that cancellation exact rather than approximate.
			const z = getBrowserZoom();
			// Exposed as a CSS var instead of mutating root.style.zoom
			// directly. Setting `zoom` on <html> itself is unreliable —
			// it doesn't always cascade the same way to every descendant
			// (this is exactly why SiteContent/SiteFooter kept scaling
			// with page zoom while DesktopHero, which applies its own
			// explicit `zoom` inline, did not). Applying the same value
			// as an explicit `zoom` on one wrapper element around the
			// *entire* visible tree (see .zoom-lock-root in Home.css)
			// gives every section — hero, content, footer — the exact
			// same counter-zoom treatment as the hero canvas already
			// gets, so they all behave identically.
			root.style.setProperty("--counter-zoom", String(1 / z));
			// Effective viewport: the on-screen space the page actually
			// occupies, expressed in (counter-zoomed) content pixels.
			const effW = window.innerWidth * z;
			const effH = window.innerHeight * z;
			root.style.setProperty("--viewport-h", `${effH}px`);
			// Media queries measure the *zoomed* viewport, so they would
			// swap layouts mid-zoom. Breakpoints are decided here against
			// the effective width and applied as classes instead (see the
			// html.bp-* rules in Home.css).
			root.classList.toggle("bp-mobile", effW <= 768);
			root.classList.toggle("bp-narrow", effW <= 900);
			root.classList.toggle("bp-tiny", effW <= 600);

			// Largest scale that fits DESIGN_WIDTH x DESIGN_HEIGHT
			// entirely inside the current viewport, so the desktop hero
			// never scrolls and looks identical (just bigger/smaller) at
			// any zoom level or window size. Shared by DesktopHero,
			// SiteContent and SiteFooter via --fit-scale.
			const isDesktop = effW > 768;
			const nextScale = isDesktop
				? Math.min(effW / DESIGN_WIDTH, effH / DESIGN_HEIGHT)
				: 1;
			root.style.setProperty("--fit-scale", String(nextScale));
			// Keep the body background glow glued to the canvas while
			// .sd-viewport centers it (see Home.css).
			root.style.setProperty(
				"--canvas-x",
				`${isDesktop ? Math.max(0, (effW - DESIGN_WIDTH * nextScale) / 2) : 0}px`,
			);
			root.style.setProperty(
				"--canvas-y",
				`${isDesktop ? Math.max(0, (effH - DESIGN_HEIGHT * nextScale) / 2) : 0}px`,
			);
		}
		function schedule() {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(update);
		}
		update();
		window.addEventListener("resize", schedule);
		window.addEventListener("orientationchange", schedule);
		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener("resize", schedule);
			window.removeEventListener("orientationchange", schedule);
			root.style.removeProperty("--counter-zoom");
		};
	}, []);
}

// Hoisted out of SeoHead — this only depends on static constants above, so
// computing it once at module load (instead of on every SeoHead render)
// also means the injection effect below can use an empty dependency array
// instead of re-diffing a freshly-allocated array/objects every render.
const JSON_LD = [
	{
		"@context": "https://schema.org",
		"@type": "MobileApplication",
		name: APP_NAME,
		url: SITE_URL,
		applicationCategory: "SportsApplication",
		operatingSystem: "iOS, Android",
		description: META_DESCRIPTION,
		image: OG_IMAGE,
		offers: {
			"@type": "Offer",
			price: "0",
			priceCurrency: "USD",
		},
		downloadUrl: [STORE_LINKS.apple, STORE_LINKS.google],
	},
	{
		"@context": "https://schema.org",
		"@type": "Organization",
		name: APP_NAME,
		url: SITE_URL,
		logo: `${SITE_URL}/assets/text.webp`,
		sameAs: [
			"https://www.facebook.com/buzzscores/",
			"https://www.instagram.com/buzzscores/",
			"https://www.tiktok.com/@buzzscores",
			STORE_LINKS.apple,
			STORE_LINKS.google,
		],
	},
	{
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: FAQS.map((faq) => ({
			"@type": "Question",
			name: faq.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: faq.answer,
			},
		})),
	},
];

// Injects the JSON-LD <script> tags into <head> imperatively instead of
// passing them as <Helmet> children. When they were Helmet children, the
// react-snap crawl ended up leaving these <script> elements behind as
// literal children of #root instead of hoisting them into <head> (visible
// by inspecting the crawled build/index.html — the static markup showed
// them sitting directly inside <div id="root">, before .zoom-lock-root).
// A real visitor's browser renders <SeoHead /> to nothing in that spot on
// its first pass (Helmet is supposed to render null in place), so that
// leftover markup was a genuine hydration mismatch — React couldn't
// reconcile "3 extra <script> nodes here" against "nothing here", threw
// React error #418, and discarded + rebuilt the entire prerendered tree
// (which is also almost certainly what caused the visible zoom/jiggle on
// load — the whole hero getting torn down and reinserted).
// Writing directly to document.head sidesteps whatever Helmet was doing
// with these specific children — this still runs during the react-snap
// crawl (so the crawled HTML still gets the structured data, correctly
// placed in <head> this time), and produces zero DOM inside #root either
// way, so there's nothing left to mismatch on hydration.
function useJsonLd(schemas) {
	useEffect(() => {
		// react-snap's static HTML already has these baked in from the crawl.
		// Without this, a real visitor's browser would run this effect AGAIN
		// on top of that prerendered markup and end up with two copies of every
		// JSON-LD block in <head>. Tagging our own nodes lets us clear any that
		// are already there (prerendered or from a previous mount) before adding
		// fresh ones, so there's always exactly one set.
		document
			.querySelectorAll('script[data-buzzscores-jsonld="true"]')
			.forEach((el) => el.remove());
		const scripts = schemas.map((schema) => {
			const el = document.createElement("script");
			el.type = "application/ld+json";
			el.dataset.buzzscoresJsonld = "true";
			el.textContent = JSON.stringify(schema);
			document.head.appendChild(el);
			return el;
		});
		return () => {
			for (const el of scripts) {
				el.remove();
			}
		};
	}, [schemas]);
}

/* ==========================================================================
   <head> tags + structured data. Rendered once; react-helmet-async merges
   it into the document head. Requires <HelmetProvider> at the app root
   and react-helmet-async installed — see SEO-SETUP.md.
   ========================================================================== */
function SeoHead() {
	useJsonLd(JSON_LD);

	return (
		<Helmet>
			<title>{META_TITLE}</title>
			<meta name="description" content={META_DESCRIPTION} />
			<link rel="canonical" href={CANONICAL_URL} />

			{/* LCP fix: this is the largest element painted on both the mobile
			    and desktop hero (the phone screenshot), and both PhoneSlideshow
			    instances already request it with loading="eager"/fetchPriority
			    "high" — but the browser still can't start the request until it
			    parses that far into the tree. A <link rel="preload"> in <head>
			    (rendered here by Helmet, so it survives into the react-snap
			    prerendered HTML too) lets the preload scanner kick the request
			    off immediately, before any of the rest of the page is parsed. */}
			<link rel="preload" as="image" href={screenshot1} fetchpriority="high" />

			{/* Open Graph */}
			<meta property="og:type" content="website" />
			<meta property="og:site_name" content={APP_NAME} />
			<meta property="og:title" content={META_TITLE} />
			<meta property="og:description" content={META_DESCRIPTION} />
			<meta property="og:url" content={CANONICAL_URL} />
			<meta property="og:image" content={OG_IMAGE} />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />

			{/* Twitter */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={META_TITLE} />
			<meta name="twitter:description" content={META_DESCRIPTION} />
			<meta name="twitter:image" content={OG_IMAGE} />

			{/* Crawling / indexing */}
			<meta name="robots" content="index, follow" />
			<meta
				name="keywords"
				content="live football scores, live soccer scores, goal replays, AI football predictions, football stats app, xG stats, Premier League scores, Champions League live scores, La Liga scores, Serie A scores, Bundesliga scores, MLS scores, football predictions app"
			/>
		</Helmet>
	);
}

// Desktop: a scan-to-download card instead of store badges.
function QrDownload() {
	return (
		<div className="qr-download">
			<img
				src={downloadQr}
				alt="QR code to download the BuzzScores live football scores app"
				className="qr-download-image"
			/>
			<span className="qr-download-label">Scan to download</span>
		</div>
	);
}

// Picks the right store link based on the visiting device, since mobile
// gets a single button instead of two separate store badges.
function getStoreLinkForDevice() {
	if (typeof navigator === "undefined") return STORE_LINKS.apple;
	const ua = navigator.userAgent || navigator.vendor || "";
	if (/android/i.test(ua)) return STORE_LINKS.google;
	if (/iphone|ipad|ipod/i.test(ua)) return STORE_LINKS.apple;
	return STORE_LINKS.apple;
}

// Mobile: single "Download app" button that routes to Google Play or the
// App Store depending on the device it's tapped from.
function DownloadAppButton() {
	const handleClick = () => {
		window.location.href = getStoreLinkForDevice();
	};

	return (
		<button
			type="button"
			className="download-app-btn"
			onClick={handleClick}
			aria-label="Download the BuzzScores app"
		>
			<span className="download-app-btn-label">
				<span className="download-app-btn-accent">DOW</span>NLOAD APP
			</span>
			<svg
				width="18"
				height="18"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
			>
				<path
					d="M12 3v11m0 0-4-4m4 4 4-4"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</button>
	);
}

function SocialIcons({ size = 18 }) {
	return (
		<>
			<a
				href="https://www.facebook.com/buzzscores/"
				aria-label="BuzzScores on Facebook"
				rel="me noopener noreferrer"
			>
				<svg
					width={size}
					height={size}
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-label="Facebook"
				>
					<path
						d="M16.5 6H13.5C12.9477 6 12.5 6.44772 12.5 7V10H16.5C16.6137 9.99748 16.7216 10.0504 16.7892 10.1419C16.8568 10.2334 16.8758 10.352 16.84 10.46L16.1 12.66C16.0318 12.8619 15.8431 12.9984 15.63 13H12.5V20.5C12.5 20.7761 12.2761 21 12 21H9.5C9.22386 21 9 20.7761 9 20.5V13H7.5C7.22386 13 7 12.7761 7 12.5V10.5C7 10.2239 7.22386 10 7.5 10H9V7C9 4.79086 10.7909 3 13 3H16.5C16.7761 3 17 3.22386 17 3.5V5.5C17 5.77614 16.7761 6 16.5 6Z"
						fill="#F0EEDF"
					/>
				</svg>
			</a>
			<a
				href="https://www.instagram.com/buzzscores/"
				aria-label="BuzzScores on Instagram"
				rel="me noopener noreferrer"
			>
				<svg
					width={size}
					height={size}
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-label="Instagram"
				>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M16 3H8C5.23858 3 3 5.23858 3 8V16C3 18.7614 5.23858 21 8 21H16C18.7614 21 21 18.7614 21 16V8C21 5.23858 18.7614 3 16 3ZM19.25 16C19.2445 17.7926 17.7926 19.2445 16 19.25H8C6.20735 19.2445 4.75549 17.7926 4.75 16V8C4.75549 6.20735 6.20735 4.75549 8 4.75H16C17.7926 4.75549 19.2445 6.20735 19.25 8V16ZM16.75 8.25C17.3023 8.25 17.75 7.80228 17.75 7.25C17.75 6.69772 17.3023 6.25 16.75 6.25C16.1977 6.25 15.75 6.69772 15.75 7.25C15.75 7.80228 16.1977 8.25 16.75 8.25ZM12 7.5C9.51472 7.5 7.5 9.51472 7.5 12C7.5 14.4853 9.51472 16.5 12 16.5C14.4853 16.5 16.5 14.4853 16.5 12C16.5027 10.8057 16.0294 9.65957 15.1849 8.81508C14.3404 7.97059 13.1943 7.49734 12 7.5ZM9.25 12C9.25 13.5188 10.4812 14.75 12 14.75C13.5188 14.75 14.75 13.5188 14.75 12C14.75 10.4812 13.5188 9.25 12 9.25C10.4812 9.25 9.25 10.4812 9.25 12Z"
						fill="#F0EEDF"
					/>
				</svg>
			</a>
			<a
				href="https://www.tiktok.com/@buzzscores"
				aria-label="BuzzScores on TikTok"
				rel="me noopener noreferrer"
			>
				<svg
					width={size}
					height={size}
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-label="Tiktok"
				>
					<path
						d="M19.3214 5.56219C19.1695 5.4837 19.0217 5.39765 18.8784 5.30438C18.4618 5.02896 18.0799 4.70445 17.7408 4.33781C16.8923 3.36703 16.5755 2.38219 16.4587 1.69266H16.4634C16.3659 1.12031 16.4063 0.75 16.4123 0.75H12.548V15.6928C12.548 15.8934 12.548 16.0917 12.5395 16.2877C12.5395 16.312 12.5372 16.3345 12.5358 16.3608C12.5358 16.3716 12.5358 16.3828 12.5334 16.3941C12.5334 16.3969 12.5334 16.3997 12.5334 16.4025C12.4927 16.9386 12.3208 17.4566 12.033 17.9107C11.7451 18.3648 11.35 18.7413 10.8825 19.0069C10.3953 19.2841 9.84417 19.4295 9.2836 19.4288C7.48313 19.4288 6.02391 17.9606 6.02391 16.1475C6.02391 14.3344 7.48313 12.8663 9.2836 12.8663C9.62442 12.8659 9.96314 12.9196 10.2872 13.0252L10.2919 9.09047C9.30814 8.9634 8.30875 9.04158 7.35674 9.32008C6.40473 9.59858 5.52077 10.0714 4.76063 10.7086C4.09456 11.2873 3.5346 11.9778 3.10594 12.7491C2.94281 13.0303 2.32734 14.1605 2.25281 15.9947C2.20594 17.0358 2.51859 18.1144 2.66766 18.5602V18.5695C2.76141 18.832 3.12469 19.7278 3.71672 20.483C4.19411 21.0887 4.75814 21.6208 5.39063 22.0622V22.0528L5.4 22.0622C7.27078 23.3334 9.345 23.25 9.345 23.25C9.70406 23.2355 10.9069 23.25 12.2728 22.6027C13.7878 21.885 14.6503 20.8158 14.6503 20.8158C15.2013 20.1769 15.6395 19.4488 15.9459 18.6628C16.2956 17.7436 16.4123 16.6411 16.4123 16.2005V8.27297C16.4592 8.30109 17.0836 8.71406 17.0836 8.71406C17.0836 8.71406 17.9831 9.29063 19.3866 9.66609C20.3934 9.93328 21.75 9.98953 21.75 9.98953V6.15328C21.2747 6.20484 20.3095 6.05484 19.3214 5.56219Z"
						fill="#F0EEDF"
					/>
				</svg>
			</a>
		</>
	);
}

/* ==========================================================================
   Hero — unchanged fixed-canvas / no-scroll visual treatment, now with
   an SEO-optimized H1 + supporting copy. Footer moved out to <SiteFooter>
   so it renders once, shared between desktop and mobile.
   ========================================================================== */

function DesktopHero() {
	return (
		<div className="sd-viewport">
			<div
				className="sd-canvas"
				style={{
					width: DESIGN_WIDTH,
					height: DESIGN_HEIGHT,
					zoom: "var(--fit-scale, 1)",
				}}
			>
				<div className="sd-columns">
					<div className="sd-visual">
						<div className="sd-glow" aria-hidden="true" />
						<PhoneSlideshow
							screenshots={SCREENSHOTS}
							size="desktop"
							width={SCREENSHOT_WIDTH}
							height={SCREENSHOT_HEIGHT}
							shiftY="-3%"
						/>
					</div>

					<div className="sd-content">
						<img
							src={logoText}
							alt="BuzzScores – live football scores and stats app"
							className="sd-logo"
						/>
						<p className="sd-eyebrow">{EYEBROW}</p>
						<h1 className="sd-headline">
							<span className="sd-headline-line">One app.</span>
							<span className="sd-headline-line">Every goal, live.</span>
						</h1>

						<div className="sd-divider" />

						<div className="sd-qr-features-row">
							<QrDownload />

							<ul className="sd-features">
								{FEATURES.map((f) => (
									<li key={f.label} className="sd-feature">
										<span className="sd-feature-icon" aria-hidden="true">
											{f.icon}
										</span>
										<span className="sd-feature-label">{f.label}</span>
									</li>
								))}
							</ul>
						</div>

						<div className="sd-divider" />
					</div>
				</div>
			</div>
		</div>
	);
}

function MobileHero() {
	return (
		<div className="sm-page">
			<img
				src={logoText}
				alt="BuzzScores – live football scores and stats app"
				className="sm-logo"
			/>

			<p className="sm-eyebrow">{EYEBROW}</p>
			<h1 className="sm-headline">
				<span className="sm-headline-line">One app.</span>
				<span className="sm-headline-line">Every goal, live.</span>
				<span className="sr-only">
					{" "}
					Live Football Scores, Goal Replays &amp; AI Predictions
				</span>
			</h1>

			<div className="sm-divider" />

			<DownloadAppButton />

			<div className="sm-phone-wrap">
				<div className="sm-glow" aria-hidden="true" />
				<PhoneSlideshow
					screenshots={SCREENSHOTS}
					size="mobile"
					shiftY="-5%"
					width={SCREENSHOT_WIDTH}
					height={SCREENSHOT_HEIGHT}
				/>
			</div>

			<ul className="sm-features">
				{FEATURES.map((f) => (
					<li key={f.label} className="sm-feature">
						<span className="sm-feature-icon" aria-hidden="true">
							{f.icon}
						</span>
						<span className="sm-feature-label">{f.label}</span>
					</li>
				))}
			</ul>

			<div className="sm-divider" />
		</div>
	);
}

/* ==========================================================================
   Below-the-fold content — rendered once, shared by desktop and mobile via
   responsive CSS. This is the crawlable copy that gives Google real,
   keyword-rich text beyond the single-viewport hero.
   ========================================================================== */

function FeaturesGrid() {
	return (
		<section className="sc-section" aria-labelledby="features-heading">
			<div className="sc-container">
				<p className="sc-eyebrow">Everything In One App</p>
				<h2 id="features-heading" className="sc-heading">
					The Smarter Way to Follow Live Football
				</h2>
				<p className="sc-lede">
					BuzzScores isn't just a score app — it's a smarter way to follow
					football. Built for fans who want real data, not just a scoreboard.
				</p>

				<div className="sc-features-grid">
					{FEATURE_DETAILS.map((f) => (
						<div className="sc-feature-card" key={f.title}>
							<span className="sc-feature-card-icon" aria-hidden="true">
								{f.icon}
							</span>
							<h3 className="sc-feature-card-title">{f.title}</h3>
							<p className="sc-feature-card-desc">{f.description}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function LeaguesSection() {
	return (
		<section
			className="sc-section sc-section-alt"
			aria-labelledby="leagues-heading"
		>
			<div className="sc-container">
				<p className="sc-eyebrow">Global Football Coverage</p>
				<h2 id="leagues-heading" className="sc-heading">
					2,000+ Leagues, Every Major Competition
				</h2>
				<p className="sc-lede">
					Follow the world's biggest and most competitive leagues, from
					continental showpieces to domestic derbies.
				</p>

				<div className="sc-leagues-grid">
					{LEAGUE_GROUPS.map((group) => (
						<div className="sc-league-group" key={group.region}>
							<h3 className="sc-league-region">{group.region}</h3>
							<ul className="sc-league-list">
								{group.leagues.map((league) => (
									<li key={league} className="sc-league-item">
										{league}
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function PremiumSection() {
	return (
		<section className="sc-section" aria-labelledby="premium-heading">
			<div className="sc-container sc-premium">
				<div className="sc-premium-copy">
					<p className="sc-eyebrow">BuzzScores Premium</p>
					<h2 id="premium-heading" className="sc-heading">
						Go Further With BuzzScores Premium
					</h2>
					<ul className="sc-premium-list">
						{PREMIUM_FEATURES.map((item) => (
							<li key={item} className="sc-premium-item">
								{item}
							</li>
						))}
					</ul>
				</div>
				<div className="sc-premium-cta">
					{/* Same desktop-view/mobile-view toggle used by the hero —
					    mobile gets the store-routing button, never the QR card. */}
					<span className="desktop-view">
						<QrDownload />
					</span>
					<span className="mobile-view">
						<DownloadAppButton />
					</span>
				</div>
			</div>
		</section>
	);
}

function FaqSection() {
	return (
		<section
			className="sc-section sc-section-alt"
			aria-labelledby="faq-heading"
		>
			<div className="sc-container">
				<p className="sc-eyebrow">Quick Answers</p>
				<h2 id="faq-heading" className="sc-heading">
					Frequently Asked Questions
				</h2>

				<div className="sc-faq-list">
					{FAQS.map((faq) => (
						<div className="sc-faq-item" key={faq.question}>
							<h3 className="sc-faq-question">{faq.question}</h3>
							<p className="sc-faq-answer">{faq.answer}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function SiteContent() {
	return (
		<main className="sc-main">
			<FeaturesGrid />
			<LeaguesSection />
			<PremiumSection />
			<FaqSection />
		</main>
	);
}

// The prerendered HTML from react-snap bakes this year in at build time.
// If the site is re-deployed less than once a year (likely), that baked-in
// year silently goes stale after every New Year's rollover and then
// mismatches the real client-computed year on *every single load* until
// the next deploy. That text mismatch is exactly what was triggering React
// error #418 (see console) — which doesn't just warn, it discards and
// re-renders the ENTIRE prerendered tree from scratch, which is what
// produced the visible whole-page "zoom in" jiggle on load. suppressHydrationWarning
// below tells React this one text node is allowed to differ without treating
// it as a fatal mismatch (so the rest of the tree hydrates normally, no
// teardown, no jiggle); the effect then corrects the visible year client-side
// right after hydration finishes.
function SiteFooter() {
	const [year, setYear] = useState(() => new Date().getFullYear());
	useEffect(() => {
		const real = new Date().getFullYear();
		setYear((prev) => (prev === real ? prev : real));
	}, []);

	return (
		<footer className="sf-footer">
			<div className="sf-footer-inner">
				<div className="sf-footer-social">
					<SocialIcons size={30} />
				</div>

				<div className="sf-footer-links">
					<Link to="/terms-of-use" className="sf-footer-link">
						Terms of use
					</Link>
					<span className="sf-footer-dot">•</span>
					<Link to="/privacypolicy" className="sf-footer-link">
						Privacy policy
					</Link>
					<span className="sf-footer-dot">•</span>
					<a href="mailto:contact@buzzscores.com" className="sf-footer-link">
						Contact us
					</a>
				</div>

				<span className="sf-footer-copyright" suppressHydrationWarning>
					© {year} BuzzScores, all rights reserved
				</span>
			</div>
		</footer>
	);
}

/* ==========================================================================
Preserves the user's relative scroll position across a zoom-triggered
resize. The zoom values that drive .zoom-lock-root / DesktopHero /
SiteContent / SiteFooter are recalculated a frame after the actual zoom
event, so by the time they take effect the browser has already done its
own (separate) scroll adjustment for the raw zoom change — our own
rescale then shifts the document's height again, which the browser
doesn't account for, showing up as a small one-notch scroll jump in
whichever direction the page just grew or shrank. Watching for actual
height changes and re-applying the same scroll *fraction* afterwards
irons that back out no matter which effect caused it or its timing.
========================================================================== */
function useScrollPositionPreserve() {
	React.useLayoutEffect(() => {
		if (typeof ResizeObserver === "undefined") return undefined;
		const doc = document.documentElement;
		function getFraction() {
			const scrollable = doc.scrollHeight - window.innerHeight;
			return scrollable > 0 ? window.scrollY / scrollable : 0;
		}
		let lastHeight = doc.scrollHeight;
		let fraction = getFraction();

		function handleScroll() {
			fraction = getFraction();
		}
		window.addEventListener("scroll", handleScroll, { passive: true });

		const ro = new ResizeObserver(() => {
			const newHeight = doc.scrollHeight;
			if (newHeight !== lastHeight) {
				const scrollable = newHeight - window.innerHeight;
				if (scrollable > 0) {
					window.scrollTo(0, fraction * scrollable);
				}
				lastHeight = newHeight;
			}
		});
		ro.observe(doc);

		return () => {
			ro.disconnect();
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);
}

function Home() {
	usePageZoomLock();
	useScrollPositionPreserve();

	return (
		<>
			<SeoHead />

			<div className="zoom-lock-root">
				<div className="desktop-view">
					<DesktopHero />
				</div>
				<div className="mobile-view">
					<MobileHero />
				</div>

				<SiteContent />
				<SiteFooter />
			</div>
		</>
	);
}

export default Home;
